import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Check,
  X,
  Crown,
  Zap,
  Calendar,
  Users,
  QrCode,
  BarChart3,
  MessageCircle,
  CreditCard,
  ExternalLink,
  AlertTriangle,
  Lock,
  Key,
} from "lucide-react";
import { API } from "@/App";
import { formatDateTR } from "@/utils/dateUtils";
import ActivationCodeModule from "@/components/ActivationCodeModule";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

const PROBODY_PAYMENT_URL = "https://probodysystems.com/fiyatlandirma";

const PLANS = [
  {
    id: "free",
    name: "Ücretsiz",
    description: "Başlangıç için",
    monthlyPrice: 0,
    yearlyPrice: 0,
    yearlyDiscount: 0,
    features: [
      { name: "Harita'da işletme profili", included: true },
      { name: "Temel işletme bilgileri", included: true },
      { name: "Mesajlaşma", included: true },
      { name: "Müşteri yorumları", included: true },
      { name: "Üye yönetimi", included: false },
      { name: "Antrenör yönetimi", included: false },
      { name: "Çalışan yönetimi", included: false },
      { name: "Rezervasyon sistemi", included: false },
      { name: "Paket satışı", included: false },
      { name: "Finans yönetimi", included: false },
      { name: "Raporlar", included: false },
    ],
    popular: false,
    color: "bg-gray-100",
  },
  {
    id: "pro",
    name: "Premium",
    description: "Tüm özellikler açık",
    monthlyPrice: 999,
    yearlyPrice: 9990,
    yearlyDiscount: 17,
    features: [
      { name: "Harita'da işletme profili", included: true },
      { name: "Temel işletme bilgileri", included: true },
      { name: "Mesajlaşma", included: true },
      { name: "Müşteri yorumları", included: true },
      { name: "Sınırsız üye yönetimi", included: true },
      { name: "Sınırsız antrenör", included: true },
      { name: "Sınırsız çalışan", included: true },
      { name: "Rezervasyon sistemi", included: true },
      { name: "Paket satışı", included: true },
      { name: "Finans yönetimi", included: true },
      { name: "Raporlar & Analitik", included: true },
      { name: "QR giriş sistemi", included: true },
      { name: "Çoklu şube desteği", included: true },
    ],
    popular: true,
    color: "bg-navy",
  },
];

export default function SubscriptionModule({ businessId, currentPlan = "free", onPlanActivated }) {
  const { t, i18n } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "reports", "qr"]);
  const { formatPrice, currencySymbol } = useCurrency();

  const [billingCycle, setBillingCycle] = useState("monthly");
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [plans, setPlans] = useState(PLANS);

  // Translate static plan data at render time
  const PT = {
    "Ücretsiz": t('dashboard:subscription.free'),
    "Başlangıç için": t('dashboard:subscription.forStarters'),
    "Tüm özellikler açık": t('dashboard:subscription.allFeaturesOpen'),
    "Harita'da işletme profili": t('dashboard:subscription.featureMapProfile'),
    "Temel işletme bilgileri": t('dashboard:subscription.featureBasicInfo'),
    "Mesajlaşma": t('common:messages'),
    "Müşteri yorumları": t('dashboard:subscription.featureReviews'),
    "Üye yönetimi": t('dashboard:subscription.memberManagement'),
    "Antrenör yönetimi": t('dashboard:subscription.trainerManagement'),
    "Çalışan yönetimi": t('dashboard:subscription.employeeManagement'),
    "Rezervasyon sistemi": t('dashboard:subscription.reservationSystem'),
    "Paket satışı": t('dashboard:subscription.packageSales'),
    "Finans yönetimi": t('dashboard:subscription.financeManagement'),
    "Raporlar": t('reports:reports'),
    "Sınırsız üye yönetimi": t('dashboard:subscription.unlimitedMembers'),
    "Sınırsız antrenör": t('dashboard:subscription.unlimitedTrainers'),
    "Sınırsız çalışan": t('dashboard:subscription.unlimitedEmployees'),
    "Raporlar & Analitik": t('dashboard:subscription.reportsAnalytics'),
    "QR giriş sistemi": t('dashboard:subscription.qrEntry'),
    "Çoklu şube desteği": t('dashboard:subscription.multiLocation'),
  };
  const translatedPlans = plans.map(p => ({
    ...p,
    name: PT[p.name] || p.name,
    description: PT[p.description] || p.description,
    features: (p.features || []).map(f => ({ ...f, name: PT[f.name] || f.name })),
  }));

  useEffect(() => {
    fetchPlans();
    if (businessId) {
      fetchSubscription();
    }
  }, [businessId]);
  
  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/public/plans`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not {plans: [...]}
        const apiPlans = Array.isArray(data) ? data : (data.plans || []);
        if (apiPlans.length > 0) {
          // Use display_features from API if available, otherwise fallback to defaults
          const mergedPlans = apiPlans.map(apiPlan => {
            const defaultPlan = PLANS.find(p => p.id === apiPlan.id) || PLANS[0];
            
            // Use display_features from API, filter to visible only
            let features = defaultPlan.features;
            if (apiPlan.display_features && apiPlan.display_features.length > 0) {
              const lang = (i18n.language || "tr").toLowerCase().split("-")[0];
              features = apiPlan.display_features
                .filter(f => f.visible !== false)
                .map(f => ({ 
                  name: (lang !== "tr" && f.translations?.[lang]) ? f.translations[lang] : f.name, 
                  included: f.included 
                }));
            }
            
            return {
              ...defaultPlan,
              id: apiPlan.id,
              name: apiPlan.name || defaultPlan.name,
              description: apiPlan.description || defaultPlan.description,
              monthlyPrice: apiPlan.monthly_price ?? defaultPlan.monthlyPrice,
              yearlyPrice: apiPlan.yearly_price ?? (apiPlan.monthly_price * 10) ?? defaultPlan.yearlyPrice,
              yearlyDiscount: apiPlan.yearly_discount ?? defaultPlan.yearlyDiscount ?? 17,
              limits: apiPlan.limits || defaultPlan.limits,
              features: features,
              popular: apiPlan.popular ?? defaultPlan.popular,
              color: apiPlan.color || defaultPlan.color,
            };
          });
          console.log("Plans loaded from API:", mergedPlans);
          setPlans(mergedPlans);
        }
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/subscription`, {
        credentials: "include",
      });
      if (response.ok) {
        setSubscription(await response.json());
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId) => {
    if (planId === "free") return;
    
    // Redirect to Probody Systems payment page
    const params = new URLSearchParams({
      plan: planId,
      billing: billingCycle,
      business_id: businessId,
      callback: window.location.origin + "/dashboard/settings?subscription=success",
    });
    
    window.open(`${PROBODY_PAYMENT_URL}?${params.toString()}`, "_blank");
  };

  const getActivePlan = () => {
    const raw = plans.find(p => p.id === (subscription?.plan || "free")) || plans[0];
    return { ...raw, name: PT[raw.name] || raw.name };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  const activePlan = getActivePlan();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="font-heading font-bold text-2xl text-navy">{t('dashboard:subscription.subscriptionManagement')}</h2>
        <p className="text-grey mt-2">{t('dashboard:subscription.managePlanOrEnterCode')}</p>
      </div>

      {/* Tabs for Plans and Activation */}
      <Tabs defaultValue="activate" className="w-full">
        <TabsList className="grid w-full grid-cols-2 rounded-xl">
          <TabsTrigger value="activate" className="rounded-xl">
            <Key className="w-4 h-4 mr-2" />
            {t('dashboard:subscription.enterCode')}
          </TabsTrigger>
          <TabsTrigger value="plans" className="rounded-xl">
            <Crown className="w-4 h-4 mr-2" />
            {t('dashboard:subscription.plans')}
          </TabsTrigger>
        </TabsList>

        {/* Activation Code Tab */}
        <TabsContent value="activate" className="mt-6">
          <ActivationCodeModule 
            businessId={businessId} 
            onSubscriptionUpdate={() => {
              fetchSubscription();
              if (onPlanActivated) onPlanActivated();
            }}
          />
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="mt-6 space-y-6">
          {/* Current Plan Status */}
          {subscription && (
            <Card className="border-2 border-navy/20 bg-navy/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className="w-6 h-6 text-navy" />
                    <div>
                      <p className="font-semibold text-navy">{t('dashboard:subscription.currentPlan')}: {activePlan.name}</p>
                      {subscription.expires_at && (
                        <p className="text-sm text-grey">
                          {t('dashboard:subscription.expiry')}: {formatDateTR(subscription.expires_at)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge className={subscription.is_active ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}>
                    {subscription.is_active ? t('admin:active') : t('common:passive')}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => setBillingCycle("monthly")}
          className={`px-4 py-2 rounded-xl font-medium transition-all ${
            billingCycle === "monthly"
              ? "bg-navy text-white"
              : "bg-gray-100 text-grey hover:bg-gray-200"
          }`}
        >
          {t('dashboard:subscription.monthly')}
        </button>
        <button
          onClick={() => setBillingCycle("yearly")}
          className={`px-4 py-2 rounded-xl font-medium transition-all ${
            billingCycle === "yearly"
              ? "bg-navy text-white"
              : "bg-gray-100 text-grey hover:bg-gray-200"
          }`}
        >
          {t('dashboard:subscription.yearly')}
          {translatedPlans.length > 0 && translatedPlans[1]?.yearlyDiscount > 0 && (
            <Badge className="ml-2 bg-green-100 text-green-600">%{translatedPlans[1]?.yearlyDiscount || 17} {t('dashboard:subscription.discount')}</Badge>
          )}
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {translatedPlans.map((plan) => (
          <Card
            key={plan.id}
            className={`relative border-2 transition-all hover:shadow-lg ${
              plan.popular ? "border-navy shadow-lg" : "border-gray-200"
            } ${activePlan.id === plan.id ? "ring-2 ring-navy ring-offset-2" : ""}`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-navy text-white px-3">
                  <Zap className="w-3 h-3 mr-1" />
                  {t('dashboard:subscription.mostPopular')}
                </Badge>
              </div>
            )}
            
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl font-bold text-navy">{plan.name}</CardTitle>
              <p className="text-sm text-grey">{plan.description}</p>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Price */}
              <div className="text-center">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-navy">
                    {currencySymbol}{billingCycle === "monthly" ? plan.monthlyPrice : Math.round(plan.yearlyPrice / 12)}
                  </span>
                  <span className="text-grey">{t('dashboard:subscription.perMonth')}</span>
                </div>
                {billingCycle === "yearly" && plan.yearlyPrice > 0 && (
                  <p className="text-sm text-grey mt-1">
                    {t('common:yearlyTotal', { total: plan.yearlyPrice.toLocaleString(), savings: (plan.monthlyPrice * 12 - plan.yearlyPrice).toLocaleString() })}
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 flex-shrink-0" />
                    )}
                    <span className={feature.included ? "text-navy" : "text-gray-400"}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                className={`w-full rounded-xl ${
                  plan.id === "free"
                    ? "bg-gray-100 text-grey hover:bg-gray-200"
                    : plan.popular
                    ? "bg-navy hover:bg-navy/90 text-white"
                    : "bg-navy/10 text-navy hover:bg-navy/20"
                }`}
                onClick={() => handleSelectPlan(plan.id)}
                disabled={activePlan.id === plan.id}
              >
                {activePlan.id === plan.id ? (
                  t('admin:currentPlanLabel')
                ) : plan.id === "free" ? (
                  t('dashboard:subscription.free')
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('dashboard:subscription.selectPlan')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment Info */}
      <Card className="border-0 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">{t("common:odemeBilgisi")}</p>
              <p className="text-sm text-amber-700 mt-1">
                {t('dashboard:subscription.paymentInfoDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature Comparison */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">{t('dashboard:subscription.planComparison')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-xl text-center">
              <Calendar className="w-6 h-6 text-navy mx-auto mb-2" />
              <p className="text-sm font-medium">{t('common:reservation')}</p>
              <p className="text-xs text-grey">Pro+</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl text-center">
              <Users className="w-6 h-6 text-navy mx-auto mb-2" />
              <p className="text-sm font-medium">{t("common:memberManagement")}</p>
              <p className="text-xs text-grey">Pro+</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl text-center">
              <QrCode className="w-6 h-6 text-navy mx-auto mb-2" />
              <p className="text-sm font-medium">{t("qr:qrGiris")}</p>
              <p className="text-xs text-grey">Pro+</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl text-center">
              <BarChart3 className="w-6 h-6 text-navy mx-auto mb-2" />
              <p className="text-sm font-medium">{t('reports:analyticsLabel')}</p>
              <p className="text-xs text-grey">Pro+</p>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Upgrade Prompt Component - Use this in restricted areas
export function UpgradePrompt({ feature, requiredPlan = "pro", businessId }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const handleUpgrade = () => {
    const params = new URLSearchParams({
      plan: requiredPlan,
      billing: "monthly",
      business_id: businessId,
      callback: window.location.origin + "/dashboard/settings?subscription=success",
    });
    
    window.open(`${PROBODY_PAYMENT_URL}?${params.toString()}`, "_blank");
  };

  return (
    <Card className="border-2 border-dashed border-navy/30 bg-navy/5">
      <CardContent className="p-8 text-center">
        <Lock className="w-12 h-12 text-navy/50 mx-auto mb-4" />
        <h3 className="font-semibold text-navy text-lg mb-2">
          {feature} - {t('dashboard:subscription.premiumFeature')}
        </h3>
        <p className="text-grey mb-4">
          {t('dashboard:subscription.upgradeRequired')}
        </p>
        <Button
          onClick={handleUpgrade}
          className="bg-navy hover:bg-navy/90 rounded-xl"
        >
          <Crown className="w-4 h-4 mr-2" />
          {t('dashboard:subscription.upgradePlan')}
        </Button>
        <p className="text-xs text-grey mt-3">
          {t('dashboard:subscription.redirectToPayment')}
        </p>
      </CardContent>
    </Card>
  );
}
